import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClassesService } from 'src/app/services/classes.service';
import { SubjectsService } from 'src/app/services/subjects.service';
import { UsersService } from 'src/app/services/users.service';
import { AuthService } from 'src/app/services/auth.service';
import { PermissionService } from 'src/app/services/permission.service';
import { WeeklyPlansService } from 'src/app/services/weekly-plans.service';
import { WeeklyPlanPdfService } from 'src/app/services/weekly-plan-pdf.service';
import { Week, WeeksService } from 'src/app/services/weeks.service';
import { SchoolSettings, SchoolSettingsService } from 'src/app/services/school-settings.service';
import {
  DEFAULT_DEPARTMENT,
  DEFAULT_SCHOOL,
  MAX_WEEKS,
  PLAN_DAYS,
  PLAN_ROWS,
  PlanEntryUi,
  PdfSubjectData,
  WeeklyPlanMeta,
  entryToUi,
  mergeEntriesWithDefaults,
  teacherDisplayName,
  uiToEntry,
  weekLabel,
  emptyGrid,
} from './weekly-plans.models';

@Component({
  selector: 'app-weekly-plans',
  templateUrl: './weekly-plans.component.html',
  styleUrls: ['./weekly-plans.component.scss']
})
export class WeeklyPlansComponent implements OnInit, OnDestroy {
  @ViewChildren('captureArea') captureAreas!: QueryList<ElementRef<HTMLElement>>;

  readonly planDays = PLAN_DAYS;
  readonly planRows = PLAN_ROWS;
  readonly weekLabel = weekLabel;
  readonly teacherDisplayName = teacherDisplayName;

  classes: any[] = [];
  subjects: any[] = [];
  teachers: any[] = [];
  weeks: Week[] = [];
  entries: PlanEntryUi[] = [];
  currentUser: any = null;
  isTeacherMode = false;
  isSupervisorMode = false;

  schoolName = DEFAULT_SCHOOL;
  departmentName = DEFAULT_DEPARTMENT;
  sectionHeadName = '';
  classId = '';
  weekOrdinal = 1;
  startDate = '';
  endDate = '';
  planId = '';

  schoolSettings: SchoolSettings = {
    schoolName: DEFAULT_SCHOOL,
    departmentName: DEFAULT_DEPARTMENT,
    headName: '',
    sectionHeads: [],
  };

  loading = true;
  savingEntryIndex: number | null = null;
  exporting = false;
  hasCopiedTable = false;
  hasCopiedCell = false;
  showClearTableModal = false;
  clearTableEntryIndex: number | null = null;
  clearTableSubjectName = '';
  cellMenu: {
    visible: boolean;
    x: number;
    y: number;
    entryIndex: number;
    rowIndex: number;
    dayIndex: number;
  } | null = null;
  statusMessage = '';
  statusParams: Record<string, unknown> = {};
  statusError = false;
  focusSubjectId = '';

  private statusTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onAnyScroll = () => this.closeCellMenu();
  private static readonly TABLE_CLIPBOARD_KEY = 'weekly-plan-table-clipboard';
  private static readonly CELL_CLIPBOARD_KEY = 'weekly-plan-cell-clipboard';

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private classesService: ClassesService,
    private subjectsService: SubjectsService,
    private usersService: UsersService,
    private weeklyPlansService: WeeklyPlansService,
    private pdfService: WeeklyPlanPdfService,
    private weeksService: WeeksService,
    private schoolSettingsService: SchoolSettingsService,
    private permissions: PermissionService,
  ) { }

  get displaySchoolName(): string {
    return this.schoolSettings.schoolName || DEFAULT_SCHOOL;
  }

  get displayDepartmentName(): string {
    return this.schoolSettings.departmentName || DEFAULT_DEPARTMENT;
  }

  get sectionHeadOptions(): string[] {
    return (this.schoolSettings.sectionHeads || [])
      .map(item => item.name?.trim())
      .filter((name): name is string => !!name);
  }

  private applySchoolSettings(): void {
    this.schoolName = this.displaySchoolName;
    this.departmentName = this.displayDepartmentName;
  }

  private applySectionHead(savedName?: string): void {
    const options = this.sectionHeadOptions;
    if (savedName && options.includes(savedName)) {
      this.sectionHeadName = savedName;
      return;
    }
    if (this.sectionHeadName && options.includes(this.sectionHeadName)) {
      return;
    }
    this.sectionHeadName = options[0] || '';
  }

  get isReadOnly(): boolean {
    return this.isSupervisorMode || !this.permissions.canEdit('weekly_plans');
  }

  get canDeletePlan(): boolean {
    return !this.isSupervisorMode && this.permissions.canDelete('weekly_plans');
  }

  /** Admin-like view of all classes/subjects, but not teacher-scoped. */
  get isAdminLikeView(): boolean {
    return !this.isTeacherMode;
  }

  get weekOptionsForSelect(): { ordinal: number; name: string }[] {
    if (this.isTeacherMode || this.isSupervisorMode) {
      return this.activeWeeks;
    }
    if (this.weeks.length) {
      return this.weeks;
    }
    return Array.from({ length: MAX_WEEKS }, (_, index) => ({
      ordinal: index + 1,
      name: weekLabel(index + 1),
    }));
  }

  get activeWeeks(): Week[] {
    return this.weeks.filter(week => week.active);
  }

  get hasMultipleActiveWeeks(): boolean {
    return this.activeWeeks.length > 1;
  }

  private selectDefaultWeek(): void {
    if (this.isTeacherMode || this.isSupervisorMode) {
      const activeWeeks = this.activeWeeks;
      if (!activeWeeks.length) {
        return;
      }
      if (!activeWeeks.some(week => week.ordinal === this.weekOrdinal)) {
        this.weekOrdinal = activeWeeks[0].ordinal;
      }
      return;
    }

    if (this.weeks.length && !this.weeks.some(week => week.ordinal === this.weekOrdinal)) {
      this.weekOrdinal = this.weeks[0].ordinal;
    }
  }

  get weekChipLabel(): string {
    const week = this.weeks.find(item => item.ordinal === this.weekOrdinal);
    return week?.name || weekLabel(this.weekOrdinal);
  }

  get weekPeriodLabel(): string {
    if (!this.startDate && !this.endDate) {
      return '';
    }
    if (this.startDate && this.endDate) {
      return `${this.fmtDisplayDate(this.startDate)} إلى ${this.fmtDisplayDate(this.endDate)}`;
    }
    return this.fmtDisplayDate(this.startDate || this.endDate);
  }

  private applyWeekDates(planFallback?: { startDate?: string; endDate?: string }): void {
    const week = this.weeks.find(item => item.ordinal === this.weekOrdinal);
    if (week) {
      this.startDate = week.startDate || planFallback?.startDate || '';
      this.endDate = week.endDate || planFallback?.endDate || '';
      return;
    }
    this.startDate = planFallback?.startDate || '';
    this.endDate = planFallback?.endDate || '';
  }

  fmtDisplayDate(value: string): string {
    if (!value) {
      return '—';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  get selectedClassName(): string {
    return this.classes.find(item => item.id === this.classId)?.name || '';
  }

  subjectsForClass: any[] = [];

  teachersForEntry(entry: PlanEntryUi): any[] {
    if (!entry.subjectId || !this.classId) {
      return [];
    }
    return this.teachers.filter(teacher =>
      teacher.subjects?.some((subject: { id: string }) => subject.id === entry.subjectId) &&
      teacher.classes?.some((cls: { id: string }) => cls.id === this.classId)
    );
  }

  private refreshSubjectsForClass(): void {
    if (this.isTeacherMode) {
      this.subjectsForClass = [...this.subjects];
      return;
    }

    if (!this.classId) {
      this.subjectsForClass = [...this.subjects];
      return;
    }
    const subjectIds = new Set<string>();
    for (const teacher of this.teachers) {
      const teachesClass = teacher.classes?.some((cls: { id: string }) => cls.id === this.classId);
      if (!teachesClass) {
        continue;
      }
      for (const subject of teacher.subjects || []) {
        subjectIds.add(subject.id);
      }
    }
    this.subjectsForClass = this.subjects.filter(subject => subjectIds.has(subject.id));
  }

  private teacherForSubject(subjectId: string): string {
    if (!subjectId || !this.classId) {
      return '';
    }
    const matches = this.teachers.filter(teacher =>
      teacher.subjects?.some((subject: { id: string }) => subject.id === subjectId) &&
      teacher.classes?.some((cls: { id: string }) => cls.id === this.classId)
    );
    return matches[0]?.id || '';
  }

  private syncEntryTeachers(): void {
    for (const entry of this.entries) {
      if (this.isTeacherMode && this.currentUser?.id) {
        entry.teacherId = this.currentUser.id;
        continue;
      }

      const matches = this.teachersForEntry(entry);
      if (!matches.length) {
        entry.teacherId = '';
        continue;
      }

      const assignedTeacher = matches.find(teacher => teacher.id === entry.teacherId);
      entry.teacherId = assignedTeacher ? assignedTeacher.id : matches[0].id;
    }
  }

  private defaultEntriesForClass(): PlanEntryUi[] {
    const subjectList = this.isTeacherMode ? this.subjects : this.subjectsForClass;
    return subjectList.map(subject => {
      const entry: PlanEntryUi = {
        subjectId: subject.id,
        subjectName: subject.name,
        teacherId: this.isTeacherMode ? (this.currentUser?.id || '') : this.teacherForSubject(subject.id),
        forcePageBreak: false,
        grid: PLAN_ROWS.map(() => PLAN_DAYS.map(() => '')),
      };
      return entry;
    });
  }

  ngOnInit(): void {
    this.isTeacherMode = this.route.snapshot.data['teacherMode'] === true
      || this.authService.isTeacher();
    this.isSupervisorMode = this.route.snapshot.data['supervisorMode'] === true
      || this.authService.isSupervisor();
    this.refreshClipboardState();
    // Capture phase so scrolling inside the table/page containers closes the menu
    document.addEventListener('scroll', this.onAnyScroll, true);
    void this.initPage();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onAnyScroll, true);
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeCellMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeCellMenu();
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    this.closeCellMenu();
  }

  private async initPage(): Promise<void> {
    this.loading = true;
    try {
      if (this.isTeacherMode) {
        const [profile, weeks, schoolSettings] = await Promise.all([
          this.authService.getProfile(),
          this.weeksService.list(),
          this.schoolSettingsService.get(),
        ]);
        this.currentUser = profile;
        this.classes = profile?.classes || [];
        this.subjects = profile?.subjects || [];
        this.teachers = profile ? [profile] : [];
        this.weeks = weeks;
        this.schoolSettings = schoolSettings;
      } else {
        const [classes, subjects, users, weeks, schoolSettings] = await Promise.all([
          this.classesService.list(),
          this.subjectsService.list(),
          this.usersService.getUsers(),
          this.weeksService.list(),
          this.schoolSettingsService.get(),
        ]);
        this.classes = classes;
        this.subjects = subjects;
        this.teachers = users.filter(user => user.role?.name === 'teacher');
        this.weeks = weeks;
        this.schoolSettings = schoolSettings;
      }

      this.applySchoolSettings();
      this.applySectionHead();
      this.selectDefaultWeek();
      this.applyQuerySelection();
      this.refreshSubjectsForClass();

      if (!this.classId && this.classes.length) {
        this.classId = this.classes[0].id;
      }

      await this.loadCurrentPlan(false);
      this.scrollToFocusedSubject();
    } catch (error: any) {
      this.showStatus(error?.error?.errorMessages?.[0]?.message || 'PLAN.LOAD_FAILED', true);
    } finally {
      this.loading = false;
    }
  }

  private applyQuerySelection(): void {
    const params = this.route.snapshot.queryParamMap;
    const classId = params.get('class');
    const weekRaw = params.get('week');
    const subjectId = params.get('subject');

    if (classId && this.classes.some(item => item.id === classId)) {
      this.classId = classId;
    }

    const weekOrdinal = weekRaw ? Number(weekRaw) : NaN;
    if (Number.isFinite(weekOrdinal) && this.weeks.some(week => week.ordinal === weekOrdinal)) {
      if (this.isSupervisorMode) {
        const week = this.weeks.find(item => item.ordinal === weekOrdinal);
        if (week?.active) {
          this.weekOrdinal = weekOrdinal;
        }
      } else {
        this.weekOrdinal = weekOrdinal;
      }
    }

    this.focusSubjectId = subjectId || '';
  }

  private scrollToFocusedSubject(): void {
    if (!this.focusSubjectId) {
      return;
    }
    const subjectId = this.focusSubjectId;
    setTimeout(() => {
      const el = document.getElementById(`plan-subject-${subjectId}`);
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('wp-subject-focus');
      setTimeout(() => el.classList.remove('wp-subject-focus'), 2500);
    }, 120);
  }

  async onClassChange(): Promise<void> {
    this.refreshSubjectsForClass();
    await this.loadCurrentPlan(true);
  }

  async onWeekChange(): Promise<void> {
    this.applyWeekDates();
    const structureSource = this.entries
      .filter(entry => this.subjectsForClass.some(subject => subject.id === entry.subjectId))
      .map(entry => ({
        subjectId: entry.subjectId,
        subjectName: entry.subjectName,
        teacherId: this.teacherForSubject(entry.subjectId),
        forcePageBreak: entry.forcePageBreak,
        grid: entry.grid.map(row => row.map(() => '')),
      }));
    await this.loadCurrentPlan(true, structureSource);
  }

  private async loadCurrentPlan(persistBeforeSwitch: boolean, structureSource?: PlanEntryUi[]): Promise<void> {
    this.refreshSubjectsForClass();

    if (!this.classId) {
      this.applyWeekDates();
      this.entries = this.defaultEntriesForClass();
      return;
    }

    try {
      const plan = await this.weeklyPlansService.getByClassAndWeek(this.classId, this.weekOrdinal);
      if (plan) {
        this.planId = plan.id || '';
        this.applySchoolSettings();
        this.applySectionHead(plan.sectionHeadName);
        this.applyWeekDates(plan);
        const savedEntries = (plan.entries || []).map(entryToUi);
        if (this.isTeacherMode) {
          this.entries = savedEntries.filter(entry =>
            this.subjects.some(subject => subject.id === entry.subjectId)
          );
          if (!this.entries.length) {
            this.entries = this.defaultEntriesForClass();
          }
        } else {
          this.entries = mergeEntriesWithDefaults(savedEntries, this.defaultEntriesForClass());
        }
        this.syncEntryTeachers();
        if (persistBeforeSwitch) {
          this.showStatus('PLAN.STATUS_LOADED_SAVED', false);
        }
        return;
      }

      this.planId = '';
      this.applySectionHead();
      this.applyWeekDates();
      const source = structureSource?.length
        ? structureSource.filter(entry => (this.isTeacherMode ? this.subjects : this.subjectsForClass)
          .some(subject => subject.id === entry.subjectId))
        : this.defaultEntriesForClass();
      this.entries = source.map(entry => ({
        ...entry,
        grid: entry.grid.map(row => row.map(() => '')),
      }));
      this.syncEntryTeachers();
      if (!this.isTeacherMode) {
        if (persistBeforeSwitch) {
          this.showStatus('PLAN.STATUS_NEW_WEEK_COPIED', false);
        }
        return;
      }
      if (persistBeforeSwitch) {
        this.showStatus('PLAN.STATUS_NEW_WEEK_FILL', false);
      }
    } catch (error: any) {
      this.showStatus(error?.error?.errorMessages?.[0]?.message || 'PLAN.PLAN_LOAD_FAILED', true);
    }
  }

  isSavingEntry(entryIndex: number): boolean {
    return this.savingEntryIndex === entryIndex;
  }

  async saveEntry(entryIndex: number): Promise<void> {
    if (this.isReadOnly) {
      return;
    }
    const entry = this.entries[entryIndex];
    if (!entry || !this.classId || this.savingEntryIndex !== null) {
      return;
    }

    this.savingEntryIndex = entryIndex;
    try {
      const plan = await this.weeklyPlansService.upsert({
        class: this.classId,
        weekOrdinal: this.weekOrdinal,
        startDate: this.startDate,
        endDate: this.endDate,
        schoolName: this.displaySchoolName,
        departmentName: this.displayDepartmentName,
        sectionHeadName: this.sectionHeadName,
        entries: [uiToEntry(entry)],
      });
      this.planId = plan.id || '';

      if (plan.entries?.length) {
        const savedEntries = plan.entries.map(entryToUi);
        if (this.isTeacherMode) {
          const saved = savedEntries.find(item => item.subjectId === entry.subjectId);
          if (saved) {
            this.entries[entryIndex] = { ...entry, ...saved, subjectName: entry.subjectName };
          }
        } else {
          this.entries = mergeEntriesWithDefaults(savedEntries, this.defaultEntriesForClass());
          this.syncEntryTeachers();
        }
      }

      this.showStatus('PLAN.STATUS_SAVED_SUBJECT', false, { subject: entry.subjectName });
    } catch (error: any) {
      this.showStatus(error?.error?.errorMessages?.[0]?.message || 'PLAN.PLAN_SAVE_FAILED', true);
    } finally {
      this.savingEntryIndex = null;
    }
  }

  copyEntryTable(entryIndex: number): void {
    const entry = this.entries[entryIndex];
    if (!entry) {
      return;
    }

    const payload = {
      version: 1,
      grid: this.copyGrid(entry.grid),
      sourceSubjectName: entry.subjectName,
      copiedAt: Date.now(),
    };

    try {
      localStorage.setItem(WeeklyPlansComponent.TABLE_CLIPBOARD_KEY, JSON.stringify(payload));
      this.hasCopiedTable = true;
      this.showStatus('PLAN.STATUS_TABLE_COPIED', false, { subject: entry.subjectName });
    } catch {
      this.showStatus('PLAN.COPY_FAILED', true);
    }
  }

  pasteEntryTable(entryIndex: number): void {
    if (this.isReadOnly) {
      return;
    }
    const entry = this.entries[entryIndex];
    const clipboard = this.readClipboard();
    if (!entry || !clipboard?.grid) {
      return;
    }

    entry.grid = this.copyGrid(clipboard.grid);
    this.showStatus('PLAN.STATUS_TABLE_PASTED', false, { subject: entry.subjectName });
  }

  requestClearEntryTable(entryIndex: number): void {
    if (this.isReadOnly) {
      return;
    }
    const entry = this.entries[entryIndex];
    if (!entry) {
      return;
    }
    this.clearTableEntryIndex = entryIndex;
    this.clearTableSubjectName = entry.subjectName;
    this.showClearTableModal = true;
  }

  onClearTableModalClose(confirmed: boolean): void {
    const entryIndex = this.clearTableEntryIndex;
    this.showClearTableModal = false;
    this.clearTableEntryIndex = null;
    const subjectName = this.clearTableSubjectName;
    this.clearTableSubjectName = '';

    if (!confirmed || entryIndex === null || this.isReadOnly) {
      return;
    }

    const entry = this.entries[entryIndex];
    if (!entry) {
      return;
    }

    entry.grid = emptyGrid();
    this.showStatus('PLAN.STATUS_TABLE_CLEARED', false, { subject: subjectName || entry.subjectName });
  }

  openCellMenu(
    event: MouseEvent,
    entryIndex: number,
    rowIndex: number,
    dayIndex: number,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.refreshClipboardState();

    const menuWidth = 180;
    const menuHeight = 96;
    const padding = 8;
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - padding);
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - padding);

    this.cellMenu = {
      visible: true,
      x: Math.max(padding, x),
      y: Math.max(padding, y),
      entryIndex,
      rowIndex,
      dayIndex,
    };
  }

  closeCellMenu(): void {
    this.cellMenu = null;
  }

  copyCellFromMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.cellMenu) {
      return;
    }
    const { entryIndex, rowIndex, dayIndex } = this.cellMenu;
    this.copyCell(entryIndex, rowIndex, dayIndex);
    this.closeCellMenu();
  }

  pasteCellFromMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.cellMenu || this.isReadOnly) {
      this.closeCellMenu();
      return;
    }
    const { entryIndex, rowIndex, dayIndex } = this.cellMenu;
    void this.pasteCell(entryIndex, rowIndex, dayIndex).finally(() => {
      this.closeCellMenu();
    });
  }

  private copyCell(entryIndex: number, rowIndex: number, dayIndex: number): void {
    const entry = this.entries[entryIndex];
    if (!entry?.grid?.[rowIndex]) {
      return;
    }

    const content = entry.grid[rowIndex][dayIndex] ?? '';
    try {
      localStorage.setItem(
        WeeklyPlansComponent.CELL_CLIPBOARD_KEY,
        JSON.stringify({ version: 1, content, copiedAt: Date.now() }),
      );
      this.hasCopiedCell = true;
      void navigator.clipboard?.writeText(content).catch(() => undefined);
    } catch {
      this.showStatus('PLAN.COPY_FAILED', true);
    }
  }

  private async pasteCell(entryIndex: number, rowIndex: number, dayIndex: number): Promise<void> {
    const entry = this.entries[entryIndex];
    if (!entry?.grid?.[rowIndex]) {
      return;
    }

    let content: string | null = null;

    // Prefer OS clipboard so paste works for text copied from outside the app
    try {
      if (navigator.clipboard?.readText) {
        content = await navigator.clipboard.readText();
      }
    } catch {
      // Permission denied / insecure context — fall back to in-app cell clipboard
    }

    if (typeof content !== 'string') {
      content = this.readCellClipboard();
    }

    if (typeof content !== 'string') {
      return;
    }

    entry.grid[rowIndex][dayIndex] = content;
  }

  exportEntryExcel(entryIndex: number): void {
    const entry = this.entries[entryIndex];
    if (!entry) {
      return;
    }

    try {
      const xml = this.buildExcelXml(entry);
      const blob = new Blob([`\uFEFF${xml}`], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeSubject = (entry.subjectName || 'subject').replace(/[\\/:*?"<>|]+/g, '-').trim();
      link.href = url;
      link.download = `${safeSubject}-week-${this.weekOrdinal}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.showStatus('PLAN.STATUS_EXCEL_SUBJECT', false, { subject: entry.subjectName });
    } catch (error: any) {
      this.showStatus(error?.message || 'PLAN.EXCEL_FAILED', true);
    }
  }

  private refreshClipboardState(): void {
    this.hasCopiedTable = !!this.readClipboard()?.grid;
    this.hasCopiedCell = this.readCellClipboard() !== null;
  }

  private readClipboard(): { grid: string[][]; sourceSubjectName?: string } | null {
    try {
      const raw = localStorage.getItem(WeeklyPlansComponent.TABLE_CLIPBOARD_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed?.grid || !Array.isArray(parsed.grid)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private readCellClipboard(): string | null {
    try {
      const raw = localStorage.getItem(WeeklyPlansComponent.CELL_CLIPBOARD_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (typeof parsed?.content !== 'string') {
        return null;
      }
      return parsed.content;
    } catch {
      return null;
    }
  }

  private escapeXml(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private buildExcelXml(entry: PlanEntryUi): string {
    const headerCells = ['العنصر', ...PLAN_DAYS]
      .map(label => `<Cell><Data ss:Type="String">${this.escapeXml(label)}</Data></Cell>`)
      .join('');

    const bodyRows = PLAN_ROWS.map((row, rowIndex) => {
      const cells = [
        `<Cell><Data ss:Type="String">${this.escapeXml(row.label)}</Data></Cell>`,
        ...PLAN_DAYS.map((_, dayIndex) => {
          const content = entry.grid?.[rowIndex]?.[dayIndex] ?? '';
          return `<Cell><Data ss:Type="String">${this.escapeXml(content)}</Data></Cell>`;
        }),
      ].join('');
      return `<Row>${cells}</Row>`;
    }).join('');

    const metaRows = [
      ['المادة', entry.subjectName],
      ['الصف', this.selectedClassName],
      ['الأسبوع', this.weekChipLabel],
      ['من', this.fmtDisplayDate(this.startDate)],
      ['إلى', this.fmtDisplayDate(this.endDate)],
      ['معلم المادة', this.teacherNameForEntry(entry)],
    ].map(([label, value]) => (
      `<Row><Cell><Data ss:Type="String">${this.escapeXml(label)}</Data></Cell>`
      + `<Cell><Data ss:Type="String">${this.escapeXml(value || '')}</Data></Cell></Row>`
    )).join('');

    return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Weekly Plan">
  <Table>
   ${metaRows}
   <Row></Row>
   <Row>${headerCells}</Row>
   ${bodyRows}
  </Table>
 </Worksheet>
</Workbook>`;
  }

  teacherNameForEntry(entry: PlanEntryUi): string {
    if (this.isTeacherMode && this.currentUser) {
      return teacherDisplayName(this.currentUser);
    }
    const teacher = this.teachersForEntry(entry).find(item => item.id === entry.teacherId)
      || this.teachers.find(item => item.id === entry.teacherId);
    return teacherDisplayName(teacher);
  }

  private buildMeta(): WeeklyPlanMeta {
    return {
      schoolName: this.displaySchoolName,
      departmentName: this.displayDepartmentName,
      classId: this.classId,
      className: this.selectedClassName,
      weekOrdinal: this.weekOrdinal,
      startDate: this.startDate,
      endDate: this.endDate,
    };
  }

  private copyGrid(grid: string[][]): string[][] {
    return PLAN_ROWS.map((_, rowIndex) =>
      PLAN_DAYS.map((_, dayIndex) => grid?.[rowIndex]?.[dayIndex] ?? '')
    );
  }

  private buildPdfSubjects(): PdfSubjectData[] {
    return this.entries.map(entry => ({
      subjectName: entry.subjectName,
      teacherName: this.teacherNameForEntry(entry),
      rows: this.copyGrid(entry.grid),
      forcePageBreak: entry.forcePageBreak,
    }));
  }

  async exportEntry(index: number): Promise<void> {
    const entry = this.entries[index];
    if (!entry) {
      return;
    }
    this.exporting = true;
    try {
      const captureEl = this.captureAreas.get(index)?.nativeElement;
      await this.pdfService.exportSubjectPdf(this.buildMeta(), {
        subjectName: entry.subjectName,
        teacherName: this.teacherNameForEntry(entry),
        rows: this.copyGrid(entry.grid),
        forcePageBreak: entry.forcePageBreak,
      }, captureEl);
      this.showStatus('PLAN.STATUS_PDF_SUBJECT', false, { subject: entry.subjectName });
    } catch (error: any) {
      this.showStatus(error?.message || 'PLAN.PDF_FAILED', true);
    } finally {
      this.exporting = false;
    }
  }

  async exportAll(): Promise<void> {
    if (!this.entries.length) {
      return;
    }
    this.exporting = true;
    try {
      const captureEls = this.captureAreas.toArray().map(item => item.nativeElement);
      await this.pdfService.exportAllPdf(
        this.buildMeta(),
        this.buildPdfSubjects(),
        {
          schoolName: this.displaySchoolName,
          departmentName: this.displayDepartmentName,
          headName: this.schoolSettings.headName,
          sectionHeads: this.sectionHeadName ? [{ name: this.sectionHeadName }] : [],
        },
        captureEls,
      );
      this.showStatus('PLAN.STATUS_PDF_FULL', false);
    } catch (error: any) {
      this.showStatus(error?.message || 'PLAN.PDF_FAILED', true);
    } finally {
      this.exporting = false;
    }
  }

  async printAll(orientation: 'landscape' | 'portrait' = 'landscape'): Promise<void> {
    if (!this.entries.length) {
      return;
    }
    this.exporting = true;
    try {
      const captureEls = this.captureAreas.toArray().map(item => item.nativeElement);
      await this.pdfService.printAllWeek(
        this.buildMeta(),
        this.buildPdfSubjects(),
        {
          schoolName: this.displaySchoolName,
          departmentName: this.displayDepartmentName,
          headName: this.schoolSettings.headName,
          sectionHeads: this.sectionHeadName ? [{ name: this.sectionHeadName }] : [],
        },
        captureEls,
        orientation,
      );
      this.showStatus('PLAN.STATUS_PRINT_FULL', false);
    } catch (error: any) {
      this.showStatus(error?.message || 'PLAN.PRINT_FAILED', true);
    } finally {
      this.exporting = false;
    }
  }

  private showStatus(message: string, isError: boolean, params: Record<string, unknown> = {}): void {
    this.statusMessage = message;
    this.statusParams = params;
    this.statusError = isError;
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }
    this.statusTimer = setTimeout(() => {
      this.statusMessage = '';
    }, isError ? 9000 : 6000);
  }
}
